// 既存のThemeSetup.jsとCompetitorAnalysis.jsを使用したリサーチフロー
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { message, Button, Space, Typography } from 'antd';
import { RocketOutlined, FormOutlined } from '@ant-design/icons';
import { getUserProfile } from '../../../auth';
import { v2AnalysisApi, publicSamplesApi } from '../../../api';
import RegisterPromptModal from '../../Demo/RegisterPromptModal';

// コンポーネント
import ThemeSetup from './ThemeSetup';
import CompetitorAnalysis from './CompetitorAnalysis';

const Analysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { analysisId } = useParams();

  // ステート管理
  const [loading, setLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [analysisLoaded, setAnalysisLoaded] = useState(false);
  const [sessionData, setSessionData] = useState({
    keyword: '',
    competitorData: null,
    step1AnalysisResult: null,
    selectedTopics: [],
    topicStrengths: {},
    selectedImportantTerms: [],
    termStrengths: {},
    requiredTopics: [],
    optionalTopics: [],
    targetAioSlots: [],
    favoriteKeywords: [],
  });

  useEffect(() => {
    loadUserProfile();

    if (analysisId && !analysisLoaded) {
      loadAnalysisResult(analysisId);
      setAnalysisLoaded(true);
    }
  }, [analysisId]);

  const loadUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsGuest(true);
      return;
    }
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setIsGuest(true);
    }
  };

  const loadAnalysisResult = async id => {
    const guest = !localStorage.getItem('token');
    setLoading(true);
    try {
      // ゲスト時は public API (同じレスポンス形式) を使用
      const apiProvider = guest ? publicSamplesApi : v2AnalysisApi;

      const statusData = await apiProvider.getAnalysisStatus(id);

      if (
        statusData.status === 'completed' ||
        statusData.status === 'completed_with_errors'
      ) {
        try {
          const result = await apiProvider.getAnalysisResult(id);
          setSessionData(prev => ({
            ...prev,
            keyword: result.keyword,
            competitorData: result,
            step1AnalysisResult: result,
          }));
        } catch (resultError) {
          console.error('Failed to get analysis result:', resultError);
          setSessionData(prev => ({
            ...prev,
            keyword: statusData.keyword || '',
            step1AnalysisResult: {
              analysis_id: id,
              keyword: statusData.keyword || '',
              status: 'completed',
              progress: 100,
              current_step: '完了',
            },
          }));
          message.warning(
            'リサーチは完了していますが、結果の詳細取得に失敗しました',
          );
        }
      } else if (
        statusData.status === 'running' ||
        statusData.status === 'started' ||
        statusData.status === 'queued'
      ) {
        setSessionData(prev => ({
          ...prev,
          keyword: statusData.keyword || '',
          step1AnalysisResult: {
            analysis_id: id,
            keyword: statusData.keyword || '',
            status: statusData.status,
            progress: statusData.progress || 0,
            current_step: statusData.current_step || 'リサーチ中...',
          },
        }));
      } else if (statusData.status === 'failed' || statusData.status === 'error') {
        const looksCompleted =
          (statusData.progress && statusData.progress >= 100) ||
          statusData.current_step === '完了';
        if (looksCompleted) {
          try {
            const result = await apiProvider.getAnalysisResult(id);
            setSessionData(prev => ({
              ...prev,
              keyword: result.keyword,
              competitorData: result,
              step1AnalysisResult: result,
            }));
          } catch (resultError) {
            setSessionData(prev => ({
              ...prev,
              keyword: statusData.keyword || '',
              step1AnalysisResult: {
                analysis_id: id,
                keyword: statusData.keyword || '',
                status: 'failed',
                progress: 100,
                current_step: '完了',
              },
            }));
            message.error(
              `リサーチ結果の取得に失敗しました: ${statusData.error_message || 'Unknown error'}`,
            );
          }
        } else {
          message.error(
            `リサーチエラー: ${statusData.error_message || 'Unknown error'}`,
          );
          navigate('/aio/analysis/new');
        }
      } else {
        setSessionData(prev => ({
          ...prev,
          keyword: statusData.keyword || '',
          step1AnalysisResult: {
            analysis_id: id,
            keyword: statusData.keyword || '',
            status: 'running',
            progress: statusData.progress || 0,
            current_step: statusData.current_step || 'リサーチ中...',
          },
        }));
      }
    } catch (error) {
      console.error('Failed to load analysis result:', error);
      message.error('リサーチ結果の読み込みに失敗しました');
      navigate('/aio/analysis/list');
    } finally {
      setLoading(false);
    }
  };

  // ポーリングは削除 - WebSocketで進捗を監視

  const updateSessionData = updates => {
    setSessionData(prev => ({
      ...prev,
      ...updates,
    }));
  };

  // 新規リサーチ画面かどうか
  const isNewAnalysis = location.pathname === '/aio/analysis/new';

  // リサーチ完了後の処理（CompetitorAnalysisのonNextの代わり）
  const handleAnalysisComplete = () => {
    // リサーチ完了後は一覧画面へ
    navigate('/aio/analysis/list');
  };

  // ゲストのアクションをインターセプトするハンドラ
  const handleGuestAction = () => {
    setShowRegisterModal(true);
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {isNewAnalysis && !sessionData.step1AnalysisResult ? (
        <ThemeSetup
          sessionData={sessionData}
          updateSessionData={updateSessionData}
          userProfile={userProfile}
          loading={loading}
          setLoading={setLoading}
          isGuest={isGuest}
          onGuestAction={handleGuestAction}
          onNext={analysisId => {
            if (analysisId) {
              navigate(`/aio/analysis/${analysisId}`);
            } else if (sessionData.step1AnalysisResult) {
              navigate(
                `/aio/analysis/${sessionData.step1AnalysisResult.analysis_id}`,
              );
            }
          }}
        />
      ) : (
        <CompetitorAnalysis
          sessionData={sessionData}
          updateSessionData={updateSessionData}
          onNext={handleAnalysisComplete}
          onPrev={() => navigate('/aio/analysis/new')}
          hideNavigation={true}
          isNewArticle={isNewAnalysis}
          isSharedView={false}
          enableProgressUI={!isGuest}
          showNavigation={!isGuest}
          showShareSettings={!isGuest}
          isGuest={isGuest}
        />
      )}

      {isGuest && (
        <RegisterPromptModal
          open={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          triggerAction="リサーチ機能"
        />
      )}

      {!isGuest && analysisId && !isNewAnalysis &&
        (sessionData.step1AnalysisResult?.status === 'completed' ||
          sessionData.step1AnalysisResult?.status === 'completed_with_errors') && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1010,
          background: 'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
        }}>
          <Space size={16}>
            <Typography.Text style={{ color: '#fff', fontSize: 14 }}>
              このリサーチ結果をもとにコンテンツを生成できます
            </Typography.Text>
            <Button
              type="primary"
              size="large"
              icon={<FormOutlined />}
              onClick={() => navigate(`/aio/generation/new?analysisId=${encodeURIComponent(analysisId)}`)}
              style={{
                background: '#fff',
                color: '#0072ff',
                border: 'none',
                fontWeight: 'bold',
                borderRadius: 8,
              }}
            >
              このリサーチを元に生成する
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default Analysis;
